import React, { useEffect } from 'react';
import './LegalPage.css';

const PrivacyPolicy = ({ onBack }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="legal-page">
      <div className="legal-nav">
        <div className="legal-logo" onClick={onBack}>
          Umpire<span>IQ</span>
        </div>
        <button className="legal-back" onClick={onBack}>← Back</button>
      </div>

      <div className="legal-inner">
        <div className="legal-header">
          <div className="legal-eyebrow">Legal</div>
          <h1>Privacy Policy</h1>
          <p className="legal-effective">Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="legal-body">

          <p>This Privacy Policy describes how GOLF DATA ASSESSORS LLC ("UmpireIQ," "we," "us," or "our"), a Wyoming limited liability company, collects, uses, and protects information obtained from users of umpire-iq.com (the "Platform"). By using the Platform, you agree to the terms of this Privacy Policy.</p>

          <h2>1. Information We Collect</h2>
          <h3>Information You Provide</h3>
          <ul>
            <li><strong>Account registration:</strong> Business email address, name, professional license number, and state of licensure.</li>
            <li><strong>Profile information:</strong> Bio, headshot, certifications, claim history, and any other information you choose to add to your profile.</li>
            <li><strong>Ratings and endorsements:</strong> Star ratings and written endorsements you submit about other platform users.</li>
            <li><strong>Peer review submissions:</strong> Redacted claim files and written narratives submitted as part of the Peer Reviewed badge application process.</li>
            <li><strong>Payment information:</strong> Billing details processed through our third-party payment processor (Stripe). We do not store full payment card information on our servers.</li>
            <li><strong>Communications:</strong> Emails and messages sent to us directly.</li>
          </ul>

          <h3>Information From Public Sources</h3>
          <p>We populate unclaimed profiles using publicly available data from the National Insurance Producer Registry (NIPR) and state Departments of Insurance or other licensing authorities. This includes name, license number, license type, license status, and state of licensure. This information is public record and may or may not be collected directly from you.</p>

          <h3>Automatically Collected Information</h3>
          <ul>
            <li>IP address and general location data</li>
            <li>Browser type and device information</li>
            <li>Pages visited and time spent on the Platform</li>
            <li>Referring URLs</li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <ul>
            <li>To create and maintain your account and profile</li>
            <li>To display your profile to other Platform users</li>
            <li>To process payments for Premium and Peer Reviewed subscriptions</li>
            <li>To facilitate the Peer Reviewed badge application and review process</li>
            <li>To send transactional emails related to your account</li>
            <li>To send platform updates and service announcements</li>
            <li>To improve and develop the Platform</li>
            <li>To detect and prevent fraud or abuse</li>
            <li>To comply with legal obligations</li>
          </ul>

          <h2>3. How We Share Your Information</h2>
          <p>We do not sell your personal information to third parties. We may share your information in the following limited circumstances:</p>
          <ul>
            <li><strong>Other users:</strong> Profile information you make public is visible to registered Platform users. Ratings and endorsements are attributed to your account as described in the Platform's settings.</li>
            <li><strong>Service providers:</strong> We work with third-party vendors including Stripe (payment processing), Cloudflare (hosting and security), and AWS (cloud infrastructure). These parties process data only as necessary to provide their services.</li>
            <li><strong>Legal process:</strong> We may disclose information in response to a valid subpoena, court order, or other legal process. We will make reasonable efforts to notify you of such requests unless prohibited by law.</li>
            <li><strong>Business transfer:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
          </ul>

          <h3>Anonymized and Aggregated Data</h3>
          <p>UmpireIQ may collect, analyze, and commercialize anonymized, aggregated data derived from Platform activity. This includes anonymized responses to Platform questionnaires and surveys, aggregated market data derived from assignment patterns, specialty distributions, and professional credentials, and statistical insights derived from rating and testimonial activity. This data does not identify any individual user and cannot reasonably be used to re-identify you. UmpireIQ may sell, license, or otherwise commercialize this anonymized data to third parties including insurers, reinsurers, industry research firms, and other market participants.</p>

          <h2>4. Peer Review File Submissions</h2>
          <p>Redacted claim files submitted as part of the Peer Reviewed badge process 
              are shared with the assigned peer review panel (three IAs and three PAs) for 
              evaluation purposes only. Peer reviewers are bound by platform terms 
              prohibiting disclosure or use of submitted materials outside the review 
              process. All submitted files must be fully redacted of personally identifiable 
              information prior to submission. UmpireIQ is not responsible for inadequate 
              redaction performed by the submitting user. Reviewers are expressly prohibited 
              from printing, screenshotting, photographing, copying, downloading, or in any 
              way duplicating or extracting review materials from the platform. All review 
              activity must occur exclusively within the platform interface.
            </p>

          <h2>5. Data Retention</h2>
          <p>We retain your account information for as long as your account is active. If you close your account, we will delete or anonymize your personal information within 90 days, except where retention is required by law or for legitimate business purposes such as resolving disputes or preventing fraud. Review submited data is permanently deleted as soon as all reviews are completed.</p>
          <p>Public license data sourced from NIPR for unclaimed profiles is retained as long as the underlying license remains active in public records.</p>

          <h2>6. Your Rights and Choices</h2>
          <ul>
            <li><strong>Access and correction:</strong> You may access and update your profile information at any time through your account settings.</li>
            <li><strong>Account deletion:</strong> Upon verified request, your active user account will be deactivated. Your private, encrypted personally identifiable information (including your account password, private address, and personal phone number) will be permanently deleted from our active databases. 
                To preserve the platform’s professional record and prevent duplicate submissions, any ratings or testimonials you authored will be retained. However, your personal name will be removed from public display on those submissions and replaced with a generic industry identifier (e.g., "Verified Public Adjuster"). If you choose to reactivate or reclaim your profile in the future, your previously authored content will automatically re-link to your account display.</li>
            <li><strong>Marketing emails:</strong> You may opt out of non-transactional emails at any time using the unsubscribe link in any email we send.</li>
          </ul>

          <li><strong>California residents (CCPA):</strong> If you are a California resident, you have the right to know what personal information we collect, the right to delete your personal information, and the right to opt out of the sale of personal information. As noted above, we do not sell PII. To exercise your rights, contact admin@umpire-iq.com.</li>

          <h2>7. Security</h2>
          <p>We implement industry-standard security measures to protect your information, including SSL/TLS encryption, secure cloud infrastructure, and access controls. No method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your information.</p>

          <h2>8. Cookies</h2>
          <p>We use cookies and similar technologies to maintain session state, remember your preferences, and analyze Platform usage. You may disable cookies in your browser settings, though this may affect Platform functionality.</p>

          <h2>9. Children's Privacy</h2>
          <p>The Platform is intended exclusively for licensed insurance professionals. 
              Users must be 18 years of age or older to register. Any account found to 
              belong to a minor will be immediately terminated without notice and all 
              associated data permanently deleted. If you become aware of a minor 
              accessing the Platform please notify us immediately at admin@umpire-iq.com.</p>

          <h2>10. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify registered users of material changes by email. Continued use of the Platform after changes take effect constitutes acceptance of the updated policy.</p>

          <h2>11. Governing Law</h2>
          <p>This Privacy Policy is governed by the laws of the State of Wyoming, without regard to conflict of law principles.</p>

          <h2>12. Contact</h2>
          <p>Questions about this Privacy Policy should be directed to:</p>
          <p><strong>GOLF DATA ASSESSORS LLC</strong><br />
          5830 E 2nd St, Ste 7000 #35896<br />
          Casper, Wyoming 82609<br />
          admin@umpire-iq.com</p>

        </div>
      </div>

      <footer className="legal-footer">
        <div className="legal-footer-logo" onClick={onBack}>Umpire<span>IQ</span></div>
        <div className="legal-footer-copy">© {new Date().getFullYear()} GOLF DATA ASSESSORS LLC. All rights reserved.</div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
